import { TypeSystem } from "../../support/type-system.support";
import { Address } from "./address.model";
import { PersonalReference } from "./personal-reference.model";
import { UserFile } from "./user-file.model";

    /**
     * @description
     *  Modelo de un usuario.
     *
     * @see gigot-backend/ViewModels/ApplicationUser/UserDto.cs
     */

export class User {

    public address: Address;
    public assignedLevelId: number;
    public assignedDivisionLevelId: number;
    public assignedLevelName: string;
    public authorizePhonesDisclosure: boolean;
    public campaignFirstOrderName: string;
    public campaignLastRestartName: string;
    public campaignLastTerminationName: string;
    public codeRoute: number;
    public condicionIVA: string;
    public creationDate: Date;
    public creditType: string;
    public cuil: string;
    public birthDate: Date;
    public documentNumber: string;
    public documentType: string;
    public email: string;
    public emailConfirmed: boolean;
    public entornoLogistico: boolean;
    public firstName: string;
    public geoLocatedZoneLevelId: string;
    public geoLocatedZoneNombre: string;
    public homePhone: string;
    public id: string;
    public inactiveCampaigns: number;
    public isActive: boolean;
    public isFounder: boolean;
    public lastName: string;
    public leaderLevelId: number;
    public leaderLevelName: string;
    public nationality: string;
    public personalReferences: PersonalReference[];
    public phoneNumber: string;
    public phoneNumberConfirmed: string;
    public profileCompletionDate: Date;
    public profileStatusType: string;
    public referrerFullName: string;
    public referrerGift: string;
    public referrerId: string;
    public referrerUserName: string;
    public resellerStatus: string;
    public roles: string[];
    public routeId: number;
    public sequence: number;
    public sex: string;
    public userFiles: UserFile[];
    public userName: string;
    public userStatus: string;
    public wantToReceiveNewsletter: boolean;

    /**
     * @description
     *  Construye un usuario con una semántica vacía. Nótese que no es lo
     *  mismo que construir un usuario con valores por defecto. Las reglas
     *  generales de construcción son:
     *
     *      - Campos 'array' se definen como [].
     *      - Campos 'boolean' se definen como 'false'.
     *      - Campos 'Date' se definen como 'undefined'.
     *      - Campos 'enum' se definen como 'undefined'.
     *      - Campos 'number' se definen como 'undefined'.
     *      - Campos 'string' se definen como '' (cadena vacía).
     *      - Campos 'object' se definen llamando a su propio método #empty().
     *      - Campos de la superclase se definen al igual que campos 'object'.
     *      - Campos genéricos se definen como 'undefined'.
	 *		- Campos recursivos se definen como 'undefined'.
     *
     * @returns
     *	Un usuario vacío.
     */
    public static empty(): User {
        const user: User = new User();
        user.address = Address.empty();
        user.assignedLevelId = undefined;
        user.assignedDivisionLevelId = undefined;
        user.assignedLevelName = "";
        user.authorizePhonesDisclosure = false;
        user.campaignFirstOrderName = "";
        user.campaignLastRestartName = "";
        user.campaignLastTerminationName = "";
        user.codeRoute = undefined;
        user.condicionIVA = "";
        user.creationDate = undefined;
        user.creditType = "";
        user.cuil = "";
        user.birthDate = undefined;
        user.documentNumber = "";
        user.documentType = "";
        user.email = "";
        user.emailConfirmed = false;
        user.entornoLogistico = false;
        user.firstName = "";
        user.geoLocatedZoneLevelId = "";
        user.geoLocatedZoneNombre = "";
        user.homePhone = "";
        user.id = "";
        user.inactiveCampaigns = undefined;
        user.isActive = false;
        user.isFounder = false;
        user.lastName = "";
        user.leaderLevelId = undefined;
        user.leaderLevelName = "";
        user.nationality = "";
        user.personalReferences = [];
        user.phoneNumber = "";
        user.phoneNumberConfirmed = "";
        user.profileCompletionDate = undefined;
        user.profileStatusType = "";
        user.referrerFullName = "";
        user.referrerGift = "";
        user.referrerId = "";
        user.referrerUserName = "";
        user.resellerStatus = "";
        user.roles = [];
        user.routeId = undefined;
        user.sequence = undefined;
        user.sex = "";
        user.userFiles = [];
        user.userName = "";
        user.userStatus = "";
        user.wantToReceiveNewsletter = false;
        return user;
    }

	/**
	 * @description
	 *  Método de reificación de un modelo compatible anémico. Esto permite
	 *  ejecutar lógica de negocios sobre el modelo generado, siendo que el
	 *  valor de sus propiedades se mantiene.
	 *
	 * @param anemicOrder
	 *  El modelo anémico a reificar.
	 *
	 * @returns
	 *  Devuelve un modelo de esta clase, cuyas propiedades fueron copiadas en
	 *  profundidad desde el modelo anémico.
	 */
	public static from(anemicUser: User): User {
		if (!anemicUser) return undefined;
		const user: User = User.empty();
		user.address = Address.from(anemicUser.address);
        user.assignedLevelId = anemicUser.assignedLevelId;
        user.assignedLevelName = anemicUser.assignedLevelName;
        user.authorizePhonesDisclosure = anemicUser.authorizePhonesDisclosure;
        user.campaignFirstOrderName = anemicUser.campaignFirstOrderName;
        user.campaignLastRestartName = anemicUser.campaignLastRestartName;
        user.campaignLastTerminationName = anemicUser.campaignLastTerminationName;
        user.codeRoute = anemicUser.codeRoute;
        user.condicionIVA = anemicUser.condicionIVA;
        user.creationDate = new Date(anemicUser.creationDate);
        user.creditType = anemicUser.creditType;
        user.cuil = anemicUser.cuil;
        user.birthDate = new Date(anemicUser.birthDate);
        user.documentNumber = anemicUser.documentNumber;
        user.documentType = anemicUser.documentType;
        user.email = anemicUser.email;
        user.emailConfirmed = anemicUser.emailConfirmed;
        user.entornoLogistico = anemicUser.entornoLogistico;
        user.firstName = anemicUser.firstName;
        user.geoLocatedZoneLevelId = anemicUser.geoLocatedZoneLevelId;
        user.geoLocatedZoneNombre = anemicUser.geoLocatedZoneNombre;
        user.homePhone = anemicUser.homePhone;
        user.id = anemicUser.id;
        user.inactiveCampaigns = anemicUser.inactiveCampaigns;
        user.isActive = anemicUser.isActive;
        user.isFounder = anemicUser.isFounder;
        user.lastName = anemicUser.lastName;
        user.leaderLevelId = anemicUser.leaderLevelId;
        user.leaderLevelName = anemicUser.leaderLevelName;
        user.nationality = anemicUser.nationality;
        user.personalReferences = TypeSystem.orElse(anemicUser.personalReferences, [])
			.map(personalReference => PersonalReference.from(personalReference));
        user.phoneNumber = anemicUser.phoneNumber;
        user.phoneNumberConfirmed = anemicUser.phoneNumberConfirmed;
        user.profileCompletionDate = new Date(anemicUser.profileCompletionDate);
        user.profileStatusType = anemicUser.profileStatusType;
        user.referrerFullName = anemicUser.referrerFullName;
        user.referrerGift = anemicUser.referrerGift;
        user.referrerId = anemicUser.referrerId;
        user.referrerUserName = anemicUser.referrerUserName;
        user.resellerStatus = anemicUser.resellerStatus;
        user.roles = TypeSystem.orElse(anemicUser.roles, []).slice();
        user.routeId = anemicUser.routeId;
        user.sequence = anemicUser.sequence;
        user.sex = anemicUser.sex;
        user.userFiles = TypeSystem.orElse(anemicUser.userFiles, [])
			.map(userFile => UserFile.from(userFile));
        user.userName = anemicUser.userName;
        user.userStatus = anemicUser.userStatus;
        user.wantToReceiveNewsletter = anemicUser.wantToReceiveNewsletter;
		return user;
	}
}

export class ResellerInfo {
    public areaLevelId: number;
    public divisionLevelId: number;
    public zoneLevelId: number;
    public divisionIdGD: number;
    public leaderLevelId: number;
}
